/**
 * usePlanetDragInteraction - Integration hook for planet drag
 *
 * Simplified event-driven version:
 * - Initializes GestureHandler (long press detection)
 * - Delegates state management to usePlanetDragState (event-driven)
 * - Chart rerender handled by PlanetDragIntegrationService on Apply
 *
 * Event flow:
 * 1. GestureHandler detects long press → emits 'planet.drag.gesture.start'
 * 2. usePlanetDragState handles loading → emits 'planet.drag.ready'
 * 3. Overlay shows navigation UI → emits 'planet.drag.navigate'
 * 4. usePlanetDragState handles navigation → emits 'planet.drag.move'
 * 5. Apply button → emits 'planet.drag.end'
 * 6. usePlanetDragState confirms → emits 'planet.drag.confirmed'
 * 7. PlanetDragIntegrationService recalculates chart → standard rerender
 */

import { devLog } from '../utils/devLog';
import { useEffect, useRef } from 'react';
import { usePlanetDragState } from './usePlanetDragState';
import { PlanetDragGestureHandler } from '../services/PlanetDragGestureHandler';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { BaseEvent } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
// Import to initialize highlight service (auto-initializes on import)
import '../services/PlanetDragHighlightService';

const DEBUG = true;

// =============================================================================
// Types
// =============================================================================

interface ChartLocation {
  lat: number;
  lon: number;
  timezoneOffset: number;
  chartDateTime: Date;  // Chart's date/time (not current browser time)
}

interface UsePlanetDragInteractionResult {
  isLoading: boolean;
  isDragging: boolean;
  loadingProgress: number;
  currentPlanet: string | null;
  selectedTime: Date | null;
  canUndo: boolean;
  undo: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function usePlanetDragInteraction(
  chartId: string,
  location: ChartLocation
): UsePlanetDragInteractionResult {
  // State management (event-driven)
  const { state, isLoading, isDragging, canUndo, undo } = usePlanetDragState(chartId, location);

  // Ref for gesture handler
  const gestureHandlerRef = useRef<PlanetDragGestureHandler | null>(null);

  // ===========================================================================
  // Effect: Subscribe to lines.ready, then setup gesture handler
  // ===========================================================================

  useEffect(() => {
    if (DEBUG) devLog(`[PlanetDrag:Hook] 📡 Subscribing to lines.ready for chart ${chartId}`);

    const handleLinesReady = (event: BaseEvent) => {
      const eventChartId = event.metadata?.chartId;
      if (eventChartId !== chartId) return;

      // Already initialized? Skip creation, handler will re-attach via its own lines.ready subscription
      if (gestureHandlerRef.current) {
        return;
      }

      if (DEBUG) devLog(`[PlanetDrag:Hook] 🔧 lines.ready received, initializing gesture handler for chart ${chartId}`);

      gestureHandlerRef.current = new PlanetDragGestureHandler(chartId);
    };

    const subscription = globalEventStream.subscribe('lines.ready', handleLinesReady);

    return () => {
      if (DEBUG) devLog(`[PlanetDrag:Hook] 🧹 Cleaning up for chart ${chartId}`);
      subscription.unsubscribe();
      gestureHandlerRef.current?.destroy();
      gestureHandlerRef.current = null;
    };
  }, [chartId]);

  // ===========================================================================
  // Computed values
  // ===========================================================================

  const loadingProgress = state.type === 'loading' ? state.progress : 0;

  const currentPlanet =
    state.type === 'loading' ? state.planet :
    state.type === 'ready' ? state.planet :
    state.type === 'dragging' ? state.planet :
    state.type === 'releasing' ? state.planet : null;

  const selectedTime = state.type === 'releasing' ? state.selectedTime : null;

  return {
    isLoading,
    isDragging,
    loadingProgress,
    currentPlanet,
    selectedTime,
    canUndo,
    undo,
  };
}
